import { useMemo, useState } from 'react';
import { navigate } from '@/hooks/useRoute';

type BuilderState = {
  style: string;
  metal: string;
  shape: string;
  carat: string;
  budget: string;
};

const steps = [
  { key: 'style', title: 'בחרו סגנון', shortTitle: 'סגנון', options: ['סוליטר', 'אבני צד', 'היילו', 'שלוש אבנים'] },
  { key: 'metal', title: 'בחרו מתכת', shortTitle: 'מתכת', options: ['זהב לבן', 'זהב צהוב', 'זהב ורוד'] },
  { key: 'shape', title: 'בחרו צורת יהלום', shortTitle: 'צורת יהלום', options: ['עגול', 'אובל', 'אמרלד', 'רדיאנט', 'טיפה'] },
  { key: 'carat', title: 'בחרו קראט', shortTitle: 'קראט', options: ['0.50 CT', '1.00 CT', '1.50 CT', '2.00 CT', '3.00 CT'] },
  { key: 'budget', title: 'בחרו תקציב', shortTitle: 'תקציב', options: ['עד 5,000 ₪', '5,000–8,000 ₪', '8,000–12,000 ₪', '12,000 ₪ ומעלה'] },
] as const;

const descriptions: Record<string, string> = {
  style: 'בחרו את מבנה הטבעת. התמונה הריאליסטית מתעדכנת מיד לפי הסגנון.',
  metal: 'בחרו זהב לבן, צהוב או ורוד — התמונה הריאליסטית מתחלפת מיד.',
  shape: 'בחרו את צורת היהלום — בסוליטר התמונה הריאליסטית מתחלפת מיד לפי הבחירה.',
  carat: 'בחרו את הקראט. בשלב זה התצוגה משנה קנה מידה להמחשה, והבחירה נשמרת.',
  budget: 'הגדירו מסגרת נוחה. את מנגנון המחיר המדויק נחבר לאחר שנשלים את סט התמונות.',
};

const styleSlug: Record<string, string> = {
  'סוליטר': 'solitaire',
  'היילו': 'halo',
  'אבני צד': 'side-stones',
  'שלוש אבנים': 'three-stone',
};

const metalSlug: Record<string, string> = {
  'זהב לבן': 'white',
  'זהב צהוב': 'yellow',
  'זהב ורוד': 'rose',
};

const shapeSlug: Record<string, string> = {
  'עגול': 'round',
  'אובל': 'oval',
  'אמרלד': 'emerald',
  'רדיאנט': 'radiant',
  'טיפה': 'pear',
};

const metalSwatch: Record<string, string> = {
  'זהב לבן': 'linear-gradient(135deg,#ffffff,#cfd2d4,#f8f8f8)',
  'זהב צהוב': 'linear-gradient(135deg,#f4d56a,#c89022,#ffe9a3)',
  'זהב ורוד': 'linear-gradient(135deg,#f2b8a2,#b86f59,#ffd2c1)',
};

const caratScale: Record<string, number> = {
  '0.50 CT': 0.90,
  '1.00 CT': 1,
  '1.50 CT': 1.035,
  '2.00 CT': 1.07,
  '3.00 CT': 1.11,
};

function imageFor(style: string, metal: string, shape: string = 'עגול') {
  return `/assets/ring-builder/ring-${styleSlug[style]}-${metalSlug[metal]}-${shapeSlug[shape] || 'round'}-1ct.png`;
}

function roundFallbackFor(style: string, metal: string) {
  return `/assets/ring-builder/ring-${styleSlug[style]}-${metalSlug[metal]}-round-1ct.png`;
}

export function RingBuilderPage() {
  const [step, setStep] = useState(1);
  const [b, setB] = useState<BuilderState>({
    style: 'סוליטר',
    metal: 'זהב לבן',
    shape: 'עגול',
    carat: '1.00 CT',
    budget: '5,000–8,000 ₪',
  });

  const current = steps[step - 1];
  const previewImage = useMemo(() => imageFor(b.style, b.metal, b.shape), [b.style, b.metal, b.shape]);

  const setChoice = (value: string) => {
    setB((prev) => ({ ...prev, [current.key]: value }));
  };

  const goNext = () => {
    if (step < steps.length) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/appointments');
    }
  };

  const goBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  return (
    <main className="rb33-page" dir="rtl">
      <style>{`
        .rb33-page{--gold:#bf9145;--gold-dark:#9b7337;--ink:#2a2520;--muted:#7e756c;--line:#e8dfd3;background:#fbfaf8;color:var(--ink);min-height:100vh;padding-bottom:72px}
        .rb33-progress{background:#fff;border-top:1px solid #eee8df;border-bottom:1px solid #e9e1d6}
        .rb33-progress-inner{max-width:1500px;margin:0 auto;padding:28px 34px 24px;display:grid;grid-template-columns:repeat(5,1fr);direction:rtl}
        .rb33-progress-step{position:relative;border:0;background:transparent;font:inherit;color:#8b837a;cursor:pointer;padding:0 8px}
        .rb33-progress-step:not(:last-child)::after{content:"";position:absolute;top:18px;left:calc(50% + 34px);width:calc(100% - 68px);height:1px;background:#ddd3c5}
        .rb33-step-circle{position:relative;z-index:2;width:38px;height:38px;border:1px solid #d9cfc1;background:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 10px}
        .rb33-progress-step.active .rb33-step-circle{background:var(--gold);color:#fff;border-color:var(--gold);box-shadow:0 5px 16px rgba(191,145,69,.18)}
        .rb33-progress-step.done .rb33-step-circle{color:var(--gold-dark);border-color:#c7a56e}
        .rb33-progress-step.done:not(:last-child)::after{background:#c7a56e}
        .rb33-progress-label{display:block;font-size:14px;white-space:nowrap}
        .rb33-progress-step.active .rb33-progress-label{color:var(--gold-dark);font-weight:600}

        .rb33-shell{max-width:1500px;margin:38px auto 0;padding:0 34px}
        .rb33-builder{display:grid;grid-template-columns:1fr 1fr;min-height:730px;background:#fff;border:1px solid #ebe2d6;border-radius:20px;overflow:hidden;box-shadow:0 14px 40px rgba(54,42,26,.055);direction:ltr}
        .rb33-preview{direction:rtl;min-width:0;background:linear-gradient(145deg,#eee8de 0%,#faf7f1 52%,#e8e0d5 100%);display:flex;flex-direction:column}
        .rb33-preview-stage{flex:1;min-height:590px;display:flex;align-items:center;justify-content:center;position:relative;padding:48px;overflow:hidden}
        .rb33-preview-stage::before{content:"";position:absolute;width:78%;aspect-ratio:1;background:radial-gradient(circle,rgba(255,255,255,.96),rgba(255,255,255,0) 68%);border-radius:50%}
        .rb33-live-badge{position:absolute;z-index:4;top:24px;right:24px;display:flex;align-items:center;gap:8px;padding:9px 14px;background:rgba(255,255,255,.92);border:1px solid rgba(166,128,66,.22);border-radius:999px;color:#5f584f;font-size:12px;letter-spacing:.08em;box-shadow:0 7px 18px rgba(73,56,31,.06)}
        .rb33-live-dot{width:7px;height:7px;border-radius:50%;background:#db5c58;box-shadow:0 0 0 4px rgba(219,92,88,.10)}
        .rb33-photo-wrap{position:relative;z-index:2;width:min(86%,650px);transition:transform .35s ease;transform-origin:center}
        .rb33-photo{width:100%;aspect-ratio:1/1;object-fit:contain;object-position:center;display:block;border-radius:18px;box-shadow:0 24px 44px rgba(72,55,33,.12);background:#f7f2eb}
        .rb33-real-chip{position:absolute;z-index:4;bottom:24px;left:24px;padding:9px 14px;border-radius:999px;background:rgba(255,255,255,.88);border:1px solid rgba(166,128,66,.18);font-size:12px;color:#665f56}
        .rb33-preview-info{background:rgba(255,255,255,.86);border-top:1px solid rgba(188,169,143,.36);padding:24px 34px 28px}
        .rb33-preview-info h2{margin:0 0 8px;font-size:27px;font-weight:500}
        .rb33-preview-meta{display:flex;gap:10px 24px;flex-wrap:wrap;color:#71685f;font-size:14px}
        .rb33-preview-sub{margin-top:13px;padding-top:13px;border-top:1px solid #eee6da;color:#998d7d;font-size:12px;line-height:1.6}
        .rb33-warning{margin-top:11px;padding:11px 13px;border-radius:10px;background:#fbf5ea;color:#8d6c38;font-size:12px;line-height:1.55}

        .rb33-config{direction:rtl;min-width:0;padding:56px 58px 42px;display:flex;flex-direction:column}
        .rb33-kicker{color:var(--gold-dark);font-size:14px;margin-bottom:15px}
        .rb33-config h1{margin:0;font-size:42px;font-weight:500;line-height:1.16;letter-spacing:-.02em}
        .rb33-rule{width:118px;height:1px;margin:22px 0 20px auto;background:linear-gradient(90deg,transparent,var(--gold),transparent)}
        .rb33-lead{margin:0 0 32px;color:var(--muted);line-height:1.8;font-size:16px}

        .rb33-style-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:13px}
        .rb33-style-card{appearance:none;border:1px solid #e6ddd1;border-radius:14px;background:#fff;padding:0;overflow:hidden;text-align:right;cursor:pointer;transition:.22s ease;min-width:0}
        .rb33-style-card:hover{transform:translateY(-3px);border-color:#cfb07c;box-shadow:0 12px 26px rgba(57,43,26,.08)}
        .rb33-style-card.active{border-color:var(--gold);box-shadow:0 0 0 1px rgba(191,145,69,.2),0 12px 26px rgba(57,43,26,.07)}
        .rb33-style-thumb{height:165px;position:relative;padding:8px;display:flex;align-items:center;justify-content:center;overflow:hidden;background:linear-gradient(180deg,#fbfaf7,#f4efe7)}
        .rb33-style-thumb img{width:100%;height:100%;object-fit:contain;object-position:center;display:block;border-radius:10px}
        .rb33-check{position:absolute;top:10px;right:10px;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--gold);color:#fff;font-size:14px}
        .rb33-style-copy{display:block;padding:16px 15px 18px}
        .rb33-style-copy strong{display:block;font-size:18px;font-weight:500;margin-bottom:7px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .rb33-style-copy span{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:36px;max-height:36px;color:#857c72;font-size:12px;line-height:1.5}

        .rb33-options{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px}
        .rb33-option{position:relative;min-height:100px;border:1px solid #e4dbcf;border-radius:13px;background:#fff;font:inherit;color:#3a342e;cursor:pointer;transition:.2s ease;padding:16px}
        .rb33-option:hover{transform:translateY(-2px);border-color:#cfb07c}
        .rb33-option.active{border-color:var(--gold);color:var(--gold-dark);background:linear-gradient(180deg,#fffdf9,#fbf4e8);box-shadow:0 0 0 1px rgba(191,145,69,.13);font-weight:600}
        .rb33-option.active::after{content:"✓";position:absolute;top:10px;right:11px;width:23px;height:23px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:var(--gold);color:#fff;font-size:12px}
        .rb33-metal-option{display:flex;align-items:center;justify-content:center;gap:10px}
        .rb33-metal-swatch{width:26px;height:26px;border-radius:50%;border:1px solid rgba(0,0,0,.12);box-shadow:inset 0 1px 3px rgba(255,255,255,.8)}
        .rb33-shape-option{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px}
        .rb33-shape-symbol{width:38px;height:38px;border:1px solid #c7ccd0;background:radial-gradient(circle at 35% 25%,#fff,#dfe5eb 68%,#b8c1ca);box-shadow:0 3px 9px rgba(74,83,92,.10)}
        .rb33-shape-symbol.round{border-radius:50%}.rb33-shape-symbol.oval{border-radius:50%;width:30px;height:42px}.rb33-shape-symbol.emerald{border-radius:5px;width:31px;height:42px}.rb33-shape-symbol.radiant{border-radius:11px;width:34px;height:40px}.rb33-shape-symbol.pear{width:31px;height:40px;border-radius:55% 55% 60% 60%;transform:rotate(45deg)}
        .rb33-carat-option{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px}
        .rb33-carat-dot{border-radius:50%;background:radial-gradient(circle at 35% 25%,#fff,#d8e0e8 70%,#aeb8c3);border:1px solid #c8cdd2;box-shadow:0 3px 9px rgba(74,83,92,.12)}

        .rb33-help-grid{margin-top:auto;padding-top:30px;display:grid;grid-template-columns:1fr 1fr;gap:12px}
        .rb33-help{border:1px solid #e9e0d4;border-radius:12px;padding:16px 17px;background:#fff;color:#5c554d;font-size:13px;display:flex;align-items:center;gap:12px}
        .rb33-help-icon{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:#fbf5ea;color:var(--gold-dark);flex:0 0 auto}
        .rb33-actions{display:flex;gap:12px;margin-top:22px}.rb33-primary,.rb33-secondary{min-height:58px;border-radius:10px;font:inherit;font-size:16px;cursor:pointer}
        .rb33-primary{flex:1;color:#fff;border:1px solid var(--gold);background:linear-gradient(110deg,#ad7f38,#d2aa66);box-shadow:0 10px 22px rgba(174,128,57,.17)}
        .rb33-secondary{width:122px;color:#5d554b;border:1px solid #ded4c6;background:#fff}

        @media(max-width:1180px){.rb33-builder{grid-template-columns:1fr}.rb33-preview{order:1}.rb33-config{order:2}.rb33-preview-stage{min-height:460px}.rb33-style-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
        @media(max-width:760px){.rb33-progress-inner{padding:18px 12px;overflow-x:auto;grid-template-columns:repeat(5,120px);justify-content:start}.rb33-progress-step:not(:last-child)::after{display:none}.rb33-shell{padding:0 12px;margin-top:18px}.rb33-builder{border-radius:14px}.rb33-config{padding:32px 20px 26px}.rb33-config h1{font-size:32px}.rb33-preview-stage{min-height:350px;padding:25px}.rb33-style-grid,.rb33-options{grid-template-columns:1fr 1fr}.rb33-help-grid{grid-template-columns:1fr}.rb33-actions{flex-direction:column}.rb33-secondary{width:100%}}
        @media(max-width:470px){.rb33-style-grid,.rb33-options{grid-template-columns:1fr}}
      `}</style>

      <nav className="rb33-progress" aria-label="שלבי בניית הטבעת">
        <div className="rb33-progress-inner">
          {steps.map((item, index) => {
            const number = index + 1;
            const active = number === step;
            const done = number < step;
            return (
              <button type="button" key={item.key} className={`rb33-progress-step ${active ? 'active' : ''} ${done ? 'done' : ''}`} onClick={() => setStep(number)}>
                <span className="rb33-step-circle">{done ? '✓' : number}</span>
                <span className="rb33-progress-label">{item.shortTitle}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <div className="rb33-shell">
        <div className="rb33-builder">
          <aside className="rb33-preview">
            <div className="rb33-preview-stage">
              <div className="rb33-live-badge"><span className="rb33-live-dot" />LIVE PREVIEW</div>
              <div className="rb33-photo-wrap" style={{ transform: `scale(${caratScale[b.carat] || 1})` }}>
                <img
                  key={previewImage}
                  className="rb33-photo"
                  src={previewImage}
                  alt={`טבעת ${b.style}, ${b.metal}, ${b.shape}, ${b.carat}`}
                  onError={(e) => {
                    const fallback = roundFallbackFor(b.style, b.metal);
                    if (e.currentTarget.src.endsWith(fallback)) return;
                    e.currentTarget.src = fallback;
                  }}
                />
              </div>
              <div className="rb33-real-chip">צילום מוצר ריאליסטי</div>
            </div>

            <div className="rb33-preview-info">
              <h2>{b.style} · {b.shape}</h2>
              <div className="rb33-preview-meta"><span>{b.metal}</span><span>{b.carat}</span><span>יהלום מעבדה</span></div>
              <div className="rb33-preview-sub">סגנון, צבע המתכת וצורת היהלום מתעדכנים מתמונות מוצר ריאליסטיות כאשר הקובץ המתאים קיים.</div>
              {b.style !== 'סוליטר' && b.shape !== 'עגול' && (
                <div className="rb33-warning">לסגנון {b.style} עדיין אין אצלנו צילום ריאליסטי לצורת {b.shape}, לכן מוצגת כרגע גרסת עגול עד שנוסיף את סט התמונות הבא.</div>
              )}
              {b.carat !== '1.00 CT' && (
                <div className="rb33-warning">בחירת {b.carat} נשמרה ומקבלת כרגע המחשת גודל. בהמשך נחבר סט צילום ייעודי לקראטים אם נחליט שהוא נדרש.</div>
              )}
            </div>
          </aside>

          <section className="rb33-config">
            <div className="rb33-kicker">שלב {step} מתוך {steps.length}</div>
            <h1>{step === 1 ? 'בחרו את הסגנון המושלם' : current.title}</h1>
            <div className="rb33-rule" />
            <p className="rb33-lead">{descriptions[current.key]}</p>

            {current.key === 'style' && (
              <div className="rb33-style-grid">
                {current.options.map((option) => (
                  <button type="button" key={option} className={`rb33-style-card ${b.style === option ? 'active' : ''}`} onClick={() => setChoice(option)}>
                    <div className="rb33-style-thumb">
                      <img
                        src={imageFor(option, b.metal, b.shape)}
                        alt={option}
                        onError={(e) => {
                          const fallback = roundFallbackFor(option, b.metal);
                          if (e.currentTarget.src.endsWith(fallback)) return;
                          e.currentTarget.src = fallback;
                        }}
                      />
                      {b.style === option && <span className="rb33-check">✓</span>}
                    </div>
                    <span className="rb33-style-copy"><strong>{option}</strong><span>{option === 'סוליטר' ? 'נקי, קלאסי ועל־זמני.' : option === 'היילו' ? 'הילה נוצצת שמגדילה את הנוכחות.' : option === 'אבני צד' ? 'ברק נוסף לצד האבן המרכזית.' : 'שלוש אבנים במראה עשיר ומאוזן.'}</span></span>
                  </button>
                ))}
              </div>
            )}

            {current.key === 'metal' && (
              <div className="rb33-options">
                {current.options.map((option) => (
                  <button type="button" key={option} className={`rb33-option rb33-metal-option ${b.metal === option ? 'active' : ''}`} onClick={() => setChoice(option)}>
                    <span className="rb33-metal-swatch" style={{ background: metalSwatch[option] }} />
                    <span>{option}</span>
                  </button>
                ))}
              </div>
            )}

            {current.key === 'shape' && (
              <div className="rb33-options">
                {current.options.map((option) => {
                  const cls = option === 'עגול' ? 'round' : option === 'אובל' ? 'oval' : option === 'אמרלד' ? 'emerald' : option === 'רדיאנט' ? 'radiant' : 'pear';
                  return (
                    <button type="button" key={option} className={`rb33-option rb33-shape-option ${b.shape === option ? 'active' : ''}`} onClick={() => setChoice(option)}>
                      <span className={`rb33-shape-symbol ${cls}`} />
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {current.key === 'carat' && (
              <div className="rb33-options">
                {current.options.map((option) => {
                  const size = 20 + (caratScale[option] || 1) * 22;
                  return (
                    <button type="button" key={option} className={`rb33-option rb33-carat-option ${b.carat === option ? 'active' : ''}`} onClick={() => setChoice(option)}>
                      <span className="rb33-carat-dot" style={{ width: size, height: size }} />
                      <span>{option}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {current.key === 'budget' && (
              <div className="rb33-options">
                {current.options.map((option) => <button type="button" key={option} className={`rb33-option ${b.budget === option ? 'active' : ''}`} onClick={() => setChoice(option)}>{option}</button>)}
              </div>
            )}

            <div className="rb33-help-grid">
              <div className="rb33-help"><span className="rb33-help-icon">◇</span><span><strong>ייעוץ אישי</strong><br />נשמח לעזור לכם לבחור את השילוב המדויק.</span></div>
              <div className="rb33-help"><span className="rb33-help-icon">↶</span><span><strong>אפשר לשנות בכל שלב</strong><br />לחצו על כל שלב למעלה וחזרו לבחירה קודמת.</span></div>
            </div>

            <div className="rb33-actions">
              {step > 1 && <button type="button" className="rb33-secondary" onClick={goBack}>חזרה</button>}
              <button type="button" className="rb33-primary" onClick={goNext}>{step < steps.length ? 'המשך לשלב הבא  ←' : 'שמירה ותיאום פגישה  ←'}</button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
